/**
 * Regras puras do cadastro de ativos refeito (US-4603, Sprint 46) — Nexora Grid (frontend).
 *
 * Criado em: 02/11/2026 · Última modificação: 02/11/2026 · Versão: 1.0.0
 *
 * Estado de cada ativo para a lista e a prévia 3D (alarme com prioridade ISA-101, sem comunicação, sem telemetria),
 * a medida principal e os números do topo que filtram. Sem DOM: testadas em `testes/cadastro.test.mjs`.
 */
import { medidaPrincipal, tipoDoCadastro } from "../cco/regrasMapa.ts";
import type { Estado } from "../componentes/Tela";

export type LeituraAtivo = { medida: string; valor: number; timestamp: string };
/** O que a telemetria diz de um dispositivo: leituras por medida, se está velho e o pior alarme sem reconhecer. */
export type TelemetriaDispositivo = { leituras: Record<string, LeituraAtivo>; velho: boolean; prioridade: 1 | 2 | 3 | 4 | null; alarme?: string };
export type AtivoResumo = { situacao: string; dados: { tipo: string; device_id: string | null; setor: string } };

const POR_PRIORIDADE: Record<1 | 2 | 3 | 4, Estado> = { 1: "critico", 2: "alto", 3: "medio", 4: "baixo" };

/** Estado do ativo: sem dispositivo = sem telemetria; alarme pela prioridade; leitura velha = sem comunicação. */
export function estadoDoAtivo(ativo: AtivoResumo, tel: TelemetriaDispositivo | undefined): Estado {
  if (!ativo.dados.device_id) return "semtel";
  if (!tel || !Object.keys(tel.leituras).length) return tel?.prioridade ? POR_PRIORIDADE[tel.prioridade] : "semcom";
  if (tel.prioridade) return POR_PRIORIDADE[tel.prioridade];
  return tel.velho ? "semcom" : "normal";
}

/** A medida que melhor resume o ativo (nível do reservatório, pressão, vazão…), ou `null`. */
export function leituraPrincipal(tipo: string, tel: TelemetriaDispositivo | undefined): LeituraAtivo | null {
  if (!tel) return null;
  const medidas = Object.keys(tel.leituras);
  const equipamento = tipoDoCadastro(tipo);
  const escolhida = equipamento ? medidaPrincipal(equipamento, medidas) : [...medidas].sort()[0];
  return escolhida ? tel.leituras[escolhida] : null;
}

/** Ordem de gravidade para ordenar a lista pelo estado (o mais grave primeiro). */
export const ORDEM_ESTADO: Record<Estado, number> = { critico: 0, alto: 1, medio: 2, baixo: 3, semcom: 4, normal: 5, semtel: 6 };

export type FiltroKpi = "" | "a_validar" | "alarme" | "semcom" | "semtel";

export function passaKpi(kpi: FiltroKpi, situacao: string, estado: Estado): boolean {
  if (kpi === "a_validar") return situacao === "pendente";
  if (kpi === "alarme") return estado === "critico" || estado === "alto" || estado === "medio" || estado === "baixo";
  if (kpi === "semcom") return estado === "semcom";
  if (kpi === "semtel") return estado === "semtel";
  return true;
}

/** Números do topo: total, a validar, em alarme (críticos e altos à parte), sem comunicação, sem telemetria. */
export function contarKpis(itens: { situacao: string; estado: Estado; setor: string }[]) {
  const c = { total: itens.length, setores: new Set(itens.map((i) => i.setor).filter(Boolean)).size, a_validar: 0, alarme: 0, criticos: 0,
    altos: 0, semcom: 0, semtel: 0 };
  for (const i of itens) {
    if (i.situacao === "pendente") c.a_validar++;
    if (passaKpi("alarme", i.situacao, i.estado)) c.alarme++;
    if (i.estado === "critico") c.criticos++;
    if (i.estado === "alto") c.altos++;
    if (i.estado === "semcom") c.semcom++;
    if (i.estado === "semtel") c.semtel++;
  }
  return c;
}

/** Junta leituras, dispositivos velhos e grupos de alarme (sem reconhecer) por dispositivo. */
export function montarTelemetria(leituras: { device_id: string; medida: string; valor: number; timestamp: string }[], velhos: string[],
  grupos: { device_id: string; prioridade: 1 | 2 | 3 | 4; reconhecido?: boolean; descricao: string }[]): Map<string, TelemetriaDispositivo> {
  const mapa = new Map<string, TelemetriaDispositivo>();
  const obter = (d: string) => {
    let t = mapa.get(d);
    if (!t) { t = { leituras: {}, velho: false, prioridade: null }; mapa.set(d, t); }
    return t;
  };
  for (const l of leituras) obter(l.device_id).leituras[l.medida] = { medida: l.medida, valor: l.valor, timestamp: l.timestamp };
  for (const d of velhos) obter(d).velho = true;
  for (const g of grupos) {
    if (g.reconhecido) continue;
    const t = obter(g.device_id);
    if (t.prioridade === null || g.prioridade < t.prioridade) { t.prioridade = g.prioridade; t.alarme = g.descricao; }
  }
  return mapa;
}
